import React, { useState } from 'react';
import { useOtelComparison } from '../../hooks/useOtelSpans';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { formatCost, PROVIDER_COLORS } from "../../lib/utils";

export const ProviderComparison = ({ autoRefresh }) => {
  const [hours, setHours] = useState(24);

  const { data, isLoading, isError } = useOtelComparison({ hours }, { autoRefresh });

  // Prepare scatter plot data (cost vs latency)
  const scatterData = data?.comparison ? Object.entries(data.comparison).map(([provider, metrics]) => ({
    provider,
    x: metrics.avg_latency_ms,
    y: metrics.avg_cost_per_call * 1000, // Convert to cost per 1K for better visualization
    color: PROVIDER_COLORS[provider]?.hex || "#71717a",
  })) : [];

  const comparisonRows = data?.comparison ? Object.entries(data.comparison) : [];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Provider Comparison</h2>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Time Range:</label>
          <Select
            value={hours.toString()}
            onValueChange={(value) => setHours(parseInt(value))}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last Hour</SelectItem>
              <SelectItem value="6">Last 6 Hours</SelectItem>
              <SelectItem value="24">Last 24 Hours</SelectItem>
              <SelectItem value="168">Last Week</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load comparison data. Please check your backend connection.
          </AlertDescription>
        </Alert>
      )}

      {/* Cost vs Latency Scatter Plot */}
      <Card>
        <CardHeader>
          <CardTitle>Cost vs Latency</CardTitle>
          <CardDescription>Trade-off analysis: average cost per call vs response time</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-96 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : scatterData.length === 0 ? (
            <div className="h-96 flex items-center justify-center text-muted-foreground">
              No comparison data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="x" 
                  name="Latency" 
                  unit="ms"
                  label={{ value: 'Avg Latency (ms)', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="y" 
                  name="Cost"
                  label={{ value: 'Avg Cost per Call ($)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(3)}`}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'Latency') return [`${value}ms`, 'Avg Latency'];
                    if (name === 'Cost') return [`$${(value / 1000).toFixed(6)}`, 'Avg Cost/Call'];
                    return value;
                  }}
                  labelFormatter={(label) => `Provider: ${label}`}
                />
                <Legend />
                <Scatter name="Providers" data={scatterData}>
                  {scatterData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          )}
          {scatterData.length > 0 && (
            <div className="mt-4 flex items-center justify-center gap-6">
              {scatterData.map((item) => (
                <div key={item.provider} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm font-medium capitalize">{item.provider}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Side-by-Side Comparison</CardTitle>
          <CardDescription>Detailed metrics breakdown by provider</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : comparisonRows.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No comparison data available
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead className="text-right">Total Calls</TableHead>
                    <TableHead className="text-right">Total Cost</TableHead>
                    <TableHead className="text-right">Avg Cost/Call</TableHead>
                    <TableHead className="text-right">Avg Latency</TableHead>
                    <TableHead className="text-right">P95 Latency</TableHead>
                    <TableHead className="text-right">Avg Tokens</TableHead>
                    <TableHead className="text-right">Error Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonRows.map(([provider, metrics]) => (
                    <TableRow key={provider}>
                      <TableCell>
                        <Badge style={{ backgroundColor: PROVIDER_COLORS[provider]?.hex || "#71717a" }}>
                          {provider}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{metrics.total_calls}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCost(metrics.total_cost)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCost(metrics.avg_cost_per_call)}
                      </TableCell>
                      <TableCell className="text-right">{metrics.avg_latency_ms.toFixed(0)}ms</TableCell>
                      <TableCell className="text-right">{metrics.p95_latency_ms.toFixed(0)}ms</TableCell>
                      <TableCell className="text-right">
                        <div className="text-sm">
                          {metrics.avg_input_tokens.toFixed(0)}↑ / {metrics.avg_output_tokens.toFixed(0)}↓
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={metrics.error_rate > 0 ? 'destructive' : 'success'}>
                          {metrics.error_rate.toFixed(1)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Models by Provider */}
      <div className={`grid grid-cols-1 gap-4 ${comparisonRows.length <= 3 ? "md:grid-cols-3" : comparisonRows.length === 4 ? "md:grid-cols-4" : "md:grid-cols-3 lg:grid-cols-5"}`}>
        {comparisonRows.map(([provider, metrics]) => (
          <Card key={provider}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Badge style={{ backgroundColor: PROVIDER_COLORS[provider]?.hex || "#71717a" }}>
                  {provider}
                </Badge>
                Top Models
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(metrics.top_models || {}).slice(0, 5).map(([model, count]) => (
                  <div key={model} className="flex items-center justify-between">
                    <span className="text-sm truncate flex-1">{model}</span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
                {Object.keys(metrics.top_models || {}).length === 0 && (
                  <p className="text-sm text-muted-foreground">No data</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
